import { IconLayer, IconLayerProps } from "@deck.gl/layers";
import { Feature, Point } from "geojson";
import { useMemo } from "react";
import ReactDOMServer from "react-dom/server";
import { useIntl } from "react-intl";
import { useSelector } from "react-redux";

import { WithIntl } from "../../../main";
import Duration from "../../components/Duration";
import { getExistingLaterals } from "../../state/existingEvents";
import { timeConverter } from "../../utils/timeConverter";

import { ICON_MAPPING } from "./DeckGlConsts";

const useDischargeOrPumpLayer = (type: "PUMP" | "DISCHARGE") => {
  const intl = useIntl();
  const existingLaterals = useSelector(getExistingLaterals);

  const tooltipDischarge = (feature: Feature) => {
    const translatedTextDischarge = intl.formatMessage({
      id: "mapDischareIcon.tooltip_text_discharge",
      defaultMessage: "Discharge of:",
    });
    const translatedTextStart = intl.formatMessage({
      id: "mapDischareIcon.tooltip_text_start_time",
      defaultMessage: "Starts at:",
    });
    const translatedTextDuration = intl.formatMessage({
      id: "mapDischareIcon.tooltip_text_duration",
      defaultMessage: "Duration:",
    });
    if (feature.properties) {
      const properties = feature.properties;
      return `<div>${translatedTextDischarge} ${properties.value} ${
        (properties.units + "").includes("m3")
          ? (properties.units + "").replace("m3", "m³")
          : properties.units
      }</div><div>${translatedTextStart} ${timeConverter(
        properties.offset
      )}</div><div>${translatedTextDuration} ${ReactDOMServer.renderToStaticMarkup(
        <WithIntl>
          <Duration duration={properties.duration} />
        </WithIntl>
      )}</div`;
    }
  };
  const tooltipPump = (feature: Feature) => {
    const translatedTextDischarge = intl.formatMessage({
      id: "mapPumpIcon.tooltip_text_pump",
      defaultMessage: "Pump of:",
    });
    const translatedTextStart = intl.formatMessage({
      id: "mapPumpIcon.tooltip_text_start_time",
      defaultMessage: "Starts at:",
    });
    const translatedTextDuration = intl.formatMessage({
      id: "mapPumpIcon.tooltip_text_duration",
      defaultMessage: "Duration:",
    });
    if (feature.properties) {
      const properties = feature.properties;
      return `<div>${translatedTextDischarge} ${-properties.value} ${
        (properties.units + "").includes("m3")
          ? (properties.units + "").replace("m3", "m³")
          : properties.units
      }</div><div>${translatedTextStart} ${timeConverter(
        properties.offset
      )}</div><div>${translatedTextDuration} ${ReactDOMServer.renderToStaticMarkup(
        <WithIntl>
          <Duration duration={properties.duration} />
        </WithIntl>
      )}</div`;
    }
  };

  const showItems = useMemo(() => {
    const filteredItems = existingLaterals.filter((lateral) => {
      if (type === "DISCHARGE") {
        return lateral.value > 0;
      } else {
        // type === "PUMP"
        return lateral.value < 0;
      }
    });

    const geoJSONSArray: Feature<Point>[] = filteredItems.map((pump) => {
      return {
        type: "Feature" as const,
        properties: pump,
        geometry: pump.point as Point, // Cast necessary because def of Point says type can be any string
      };
    });

    return geoJSONSArray
      .map(
        (geoJSON) =>
          geoJSON.geometry && {
            coordinate: geoJSON.geometry.coordinates,
            tooltip: type === "DISCHARGE" ? tooltipDischarge(geoJSON) : tooltipPump(geoJSON),
          }
      )
      .filter((geoJSON) => geoJSON);
  }, [existingLaterals]);

  const defaultIconLayerProps = {
    iconAtlas: "./assets/images/iconAtlas.png",
    iconMapping: ICON_MAPPING,
    sizeUnits: "pixels",
    getSize: 50,
    getPosition: (d: any) => d.coordinate,
    pickable: true,
  } as unknown as IconLayerProps;

  return [
    new IconLayer({
      ...defaultIconLayerProps,
      data: showItems,
      id: type,
      getIcon: () => (type === "DISCHARGE" ? "dischargePin" : "pumpPin"),
    }),
  ];
};

export default useDischargeOrPumpLayer;
